import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { 
  Waves, Search, ChevronDown, ChevronUp, Edit2, Trash2, X, Save, 
  MapPin, Calendar, User, Eye, Star, Clock, Thermometer, Fish,
  Gauge, Ruler, Weight, Shirt, Camera, Building2, FileText
} from "lucide-react";
import { toast } from "sonner";
import { BACKEND_URL } from '@/utils/apiConfig';

const API = `${BACKEND_URL}/api`;

// Helper component for info rows
const InfoRow = ({ icon: Icon, label, value, unit = "" }) => {
  if (!value && value !== 0) return null;
  return (
    <div className="flex items-center gap-3 py-2">
      <Icon className="w-5 h-5 text-cyan-600 flex-shrink-0" />
      <span className="text-gray-600">{label}:</span>
      <span className="font-medium">{value}{unit}</span>
    </div>
  );
};

// Helper component for section cards
const SectionCard = ({ title, icon: Icon, children }) => (
  <Card className="mb-4">
    <CardContent className="pt-4">
      <h3 className="flex items-center gap-2 font-semibold text-gray-900 mb-3">
        <Icon className="w-5 h-5 text-cyan-600" />
        {title}
      </h3>
      {children}
    </CardContent>
  </Card>
);

const DiveReportsManagement = ({ token }) => {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [expandedId, setExpandedId] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [editData, setEditData] = useState({});
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState("dive");

  const fetchReports = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/admin/dive-reports`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setReports(response.data);
    } catch (error) {
      console.error("Failed to fetch dive reports:", error);
      toast.error("Failed to load dive reports");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchReports();
  }, [fetchReports]);

  const handleDelete = async (reportId) => {
    if (!window.confirm("Are you sure you want to delete this dive report? This action cannot be undone.")) {
      return;
    }

    try {
      await axios.delete(`${API}/dive-reports/${reportId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Dive report deleted");
      setReports(reports.filter(r => r.id !== reportId));
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete report");
    }
  };

  const handleEdit = (report) => {
    setEditingId(report.id);
    setEditData({
      title: report.title || "",
      dive_site_name: report.dive_site_name || "",
      country: report.country || "",
      dive_center_name: report.dive_center_name || "",
      notes: report.notes || "",
      depth: report.depth || "",
      dive_time: report.dive_time || "",
      rating: report.rating || ""
    });
  };

  const handleSave = async (reportId) => {
    setSaving(true);
    try {
      const updatePayload = {
        ...editData,
        depth: editData.depth ? parseFloat(editData.depth) : null,
        dive_time: editData.dive_time ? parseInt(editData.dive_time) : null,
        rating: editData.rating ? parseInt(editData.rating) : null
      };
      
      const response = await axios.put(`${API}/dive-reports/${reportId}`, updatePayload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setReports(reports.map(r => r.id === reportId ? { ...r, ...response.data } : r));
      setEditingId(null);
      toast.success("Dive report updated");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update report");
    } finally {
      setSaving(false);
    }
  };

  const getImageUrl = (url) => {
    if (!url) return null;
    return url.startsWith('http') ? url : `${BACKEND_URL}${url}`;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "N/A";
    return new Date(dateStr).toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const renderStars = (rating) => {
    if (!rating) return <span className="text-gray-400">Not rated</span>;
    return (
      <div className="flex gap-1">
        {[1, 2, 3, 4, 5].map((star) => (
          <Star
            key={star}
            className={`w-5 h-5 ${
              star <= rating ? "fill-yellow-400 text-yellow-400" : "text-gray-300"
            }`}
          />
        ))}
      </div>
    );
  };

  const filteredReports = reports.filter(report => {
    const search = searchTerm.toLowerCase();
    return (
      report.title?.toLowerCase().includes(search) ||
      report.dive_site_name?.toLowerCase().includes(search) ||
      report.country?.toLowerCase().includes(search) ||
      report.user_name?.toLowerCase().includes(search) ||
      report.dive_center_name?.toLowerCase().includes(search)
    );
  });

  if (loading) {
    return (
      <Card>
        <CardContent className="py-12 text-center">
          <p className="text-gray-500">Loading dive reports...</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Waves className="w-5 h-5 text-blue-600" />
          Dive Reports Management
          <span className="text-sm font-normal text-gray-500">({reports.length} total)</span>
        </CardTitle>
      </CardHeader>
      <CardContent>
        {/* Search */}
        <div className="relative mb-6">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input
            placeholder="Search by title, site, country, user, or dive center..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>

        {/* Reports List */}
        <div className="space-y-3">
          {filteredReports.length === 0 ? (
            <p className="text-center text-gray-500 py-8">No dive reports found</p>
          ) : (
            filteredReports.map((report) => (
              <div key={report.id} className="border rounded-lg overflow-hidden">
                {/* Report Header - Clickable */}
                <div
                  className="p-4 bg-gray-50 cursor-pointer hover:bg-gray-100 transition-colors"
                  onClick={() => {
                    setExpandedId(expandedId === report.id ? null : report.id);
                    setActiveTab("dive");
                  }}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <Waves className="w-5 h-5 text-blue-500" />
                      <div>
                        <h3 className="font-semibold text-gray-900">{report.title || "Untitled Report"}</h3>
                        <div className="flex items-center gap-3 text-sm text-gray-500 flex-wrap">
                          <span className="flex items-center gap-1">
                            <User className="w-3 h-3" />
                            {report.user_name}
                          </span>
                          <span className="flex items-center gap-1">
                            <Calendar className="w-3 h-3" />
                            {new Date(report.dive_date).toLocaleDateString()}
                          </span>
                          {report.dive_site_name && (
                            <span className="flex items-center gap-1">
                              <MapPin className="w-3 h-3" />
                              {report.dive_site_name}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="hidden md:flex items-center gap-3 text-sm text-gray-600">
                        {report.depth && <span>{report.depth}{report.depth_unit || 'm'}</span>}
                        {report.dive_time && (
                          <span className="flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            {report.dive_time}min
                          </span>
                        )}
                        {report.rating && (
                          <div className="flex">
                            {[1,2,3,4,5].map(s => (
                              <Star key={s} className={`w-3 h-3 ${s <= report.rating ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'}`} />
                            ))}
                          </div>
                        )}
                      </div>
                      {expandedId === report.id ? (
                        <ChevronUp className="w-5 h-5 text-gray-400" />
                      ) : (
                        <ChevronDown className="w-5 h-5 text-gray-400" />
                      )}
                    </div>
                  </div>
                </div>

                {/* Expanded Content */}
                {expandedId === report.id && (
                  <div className="p-4 border-t bg-white">
                    {editingId === report.id ? (
                      /* Edit Form */
                      <div className="space-y-4">
                        <div className="grid md:grid-cols-2 gap-4">
                          <div>
                            <Label>Title</Label>
                            <Input
                              value={editData.title}
                              onChange={(e) => setEditData({...editData, title: e.target.value})}
                            />
                          </div>
                          <div>
                            <Label>Dive Site Name</Label>
                            <Input
                              value={editData.dive_site_name}
                              onChange={(e) => setEditData({...editData, dive_site_name: e.target.value})}
                            />
                          </div>
                          <div>
                            <Label>Country</Label>
                            <Input
                              value={editData.country}
                              onChange={(e) => setEditData({...editData, country: e.target.value})}
                            />
                          </div>
                          <div>
                            <Label>Dive Center</Label>
                            <Input
                              value={editData.dive_center_name}
                              onChange={(e) => setEditData({...editData, dive_center_name: e.target.value})}
                            />
                          </div>
                          <div>
                            <Label>Depth ({report.depth_unit || 'm'})</Label>
                            <Input
                              type="number"
                              value={editData.depth}
                              onChange={(e) => setEditData({...editData, depth: e.target.value})}
                            />
                          </div>
                          <div>
                            <Label>Dive Time (min)</Label>
                            <Input
                              type="number"
                              value={editData.dive_time}
                              onChange={(e) => setEditData({...editData, dive_time: e.target.value})}
                            />
                          </div>
                          <div>
                            <Label>Rating (1-5)</Label>
                            <Input
                              type="number"
                              min="1"
                              max="5"
                              value={editData.rating}
                              onChange={(e) => setEditData({...editData, rating: e.target.value})}
                            />
                          </div>
                          <div className="md:col-span-2">
                            <Label>Notes</Label>
                            <Textarea
                              value={editData.notes}
                              onChange={(e) => setEditData({...editData, notes: e.target.value})}
                              rows={3}
                            />
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <Button onClick={() => handleSave(report.id)} disabled={saving}>
                            <Save className="w-4 h-4 mr-1" />
                            {saving ? "Saving..." : "Save"}
                          </Button>
                          <Button variant="outline" onClick={() => setEditingId(null)}>
                            <X className="w-4 h-4 mr-1" />
                            Cancel
                          </Button>
                        </div>
                      </div>
                    ) : (
                      /* View Content with 3 Tabs */
                      <div className="space-y-4">
                        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
                          <TabsList className="grid w-full grid-cols-3">
                            <TabsTrigger value="dive">
                              <Gauge className="w-4 h-4 mr-2" />
                              Dive
                            </TabsTrigger>
                            <TabsTrigger value="site">
                              <MapPin className="w-4 h-4 mr-2" />
                              Site
                            </TabsTrigger>
                            <TabsTrigger value="notes">
                              <FileText className="w-4 h-4 mr-2" />
                              Notes
                            </TabsTrigger>
                          </TabsList>

                          {/* DIVE TAB */}
                          <TabsContent value="dive" className="mt-4 space-y-4">
                            {/* Dive Metrics */}
                            <SectionCard title="Dive Metrics" icon={Ruler}>
                              <div className="grid grid-cols-2 gap-2">
                                <InfoRow icon={Ruler} label="Max Depth" value={report.depth} unit={` ${report.depth_unit || 'm'}`} />
                                <InfoRow icon={Clock} label="Dive Time" value={report.dive_time} unit=" min" />
                              </div>
                            </SectionCard>

                            {/* Temperature */}
                            {report.temperature && (report.temperature.outside || report.temperature.surface || report.temperature.underwater) && (
                              <SectionCard title="Temperature" icon={Thermometer}>
                                <div className="grid grid-cols-3 gap-4">
                                  {report.temperature.outside && (
                                    <div className="text-center p-3 bg-blue-50 rounded-lg">
                                      <div className="text-sm text-gray-600">Outside</div>
                                      <div className="text-xl font-semibold text-blue-600">
                                        {report.temperature.outside}°{report.temperature.unit || 'C'}
                                      </div>
                                    </div>
                                  )}
                                  {report.temperature.surface && (
                                    <div className="text-center p-3 bg-cyan-50 rounded-lg">
                                      <div className="text-sm text-gray-600">Surface</div>
                                      <div className="text-xl font-semibold text-cyan-600">
                                        {report.temperature.surface}°{report.temperature.unit || 'C'}
                                      </div>
                                    </div>
                                  )}
                                  {report.temperature.underwater && (
                                    <div className="text-center p-3 bg-teal-50 rounded-lg">
                                      <div className="text-sm text-gray-600">Underwater</div>
                                      <div className="text-xl font-semibold text-teal-600">
                                        {report.temperature.underwater}°{report.temperature.unit || 'C'}
                                      </div>
                                    </div>
                                  )}
                                </div>
                              </SectionCard>
                            )}

                            {/* Equipment */}
                            {report.equipment && Object.values(report.equipment).some(v => v) && (
                              <SectionCard title="Equipment" icon={Shirt}>
                                <div className="space-y-2">
                                  {report.equipment.gas_type && (
                                    <InfoRow 
                                      icon={Gauge} 
                                      label="Gas" 
                                      value={report.equipment.gas_type === "Nitrox" && report.equipment.nitrox_percentage 
                                        ? `Nitrox ${report.equipment.nitrox_percentage}%` 
                                        : report.equipment.gas_type} 
                                    />
                                  )}
                                  {report.equipment.cylinder_size && (
                                    <InfoRow 
                                      icon={Gauge} 
                                      label="Cylinder" 
                                      value={`${report.equipment.cylinder_size} ${report.equipment.cylinder_type || ''}`} 
                                    />
                                  )}
                                  {(report.equipment.starting_pressure || report.equipment.ending_pressure) && (
                                    <InfoRow 
                                      icon={Gauge} 
                                      label="Air" 
                                      value={`${report.equipment.starting_pressure || '?'} → ${report.equipment.ending_pressure || '?'} bar`} 
                                    />
                                  )}
                                  {report.equipment.suit_type && (
                                    <InfoRow 
                                      icon={Shirt} 
                                      label="Suit" 
                                      value={`${report.equipment.suit_type}${report.equipment.suit_thickness ? ` ${report.equipment.suit_thickness}mm` : ''}${report.equipment.suit_size ? ` (${report.equipment.suit_size})` : ''}`} 
                                    />
                                  )}
                                  {report.equipment.bcd_type && (
                                    <InfoRow 
                                      icon={Shirt} 
                                      label="BCD" 
                                      value={`${report.equipment.bcd_type}${report.equipment.bcd_size ? ` (${report.equipment.bcd_size})` : ''}`} 
                                    />
                                  )}
                                  {report.equipment.hood && (
                                    <InfoRow icon={Shirt} label="Hood" value={report.equipment.hood_size || "Yes"} />
                                  )}
                                  {report.equipment.boots && (
                                    <InfoRow icon={Shirt} label="Boots" value={report.equipment.boots_size || "Yes"} />
                                  )}
                                  {report.equipment.weights && (
                                    <InfoRow 
                                      icon={Weight} 
                                      label="Weights" 
                                      value={`${report.equipment.weights} ${report.equipment.weights_unit || 'kg'}${report.equipment.weight_type ? ` (${report.equipment.weight_type})` : ''}`} 
                                    />
                                  )}
                                </div>
                              </SectionCard>
                            )}

                            {/* Dive Profile Photo */}
                            {report.dive_profile_photo && (
                              <SectionCard title="Dive Profile Photo" icon={Camera}>
                                <img 
                                  src={getImageUrl(report.dive_profile_photo)}
                                  alt="Dive profile"
                                  className="w-full max-w-md rounded-lg"
                                />
                              </SectionCard>
                            )}
                          </TabsContent>

                          {/* SITE TAB */}
                          <TabsContent value="site" className="mt-4 space-y-4">
                            {/* Location */}
                            <SectionCard title="Location" icon={MapPin}>
                              <div className="space-y-2">
                                {report.country && (
                                  <InfoRow icon={MapPin} label="Country" value={report.country} />
                                )}
                                {report.dive_site_name && (
                                  <InfoRow icon={MapPin} label="Dive Site" value={report.dive_site_name} />
                                )}
                                {report.is_custom_site && report.custom_coordinates?.lat && (
                                  <InfoRow 
                                    icon={MapPin} 
                                    label="Coordinates" 
                                    value={`${report.custom_coordinates.lat}, ${report.custom_coordinates.lng}`} 
                                  />
                                )}
                              </div>
                            </SectionCard>

                            {/* Conditions */}
                            {report.conditions && Object.values(report.conditions).some(v => v) && (
                              <SectionCard title="Conditions" icon={Waves}>
                                <div className="grid grid-cols-2 gap-3">
                                  {report.conditions.current && (
                                    <div className="p-3 bg-blue-50 rounded-lg">
                                      <div className="text-sm text-gray-600">Current</div>
                                      <div className="font-medium">{report.conditions.current}</div>
                                    </div>
                                  )}
                                  {report.conditions.waves && (
                                    <div className="p-3 bg-blue-50 rounded-lg">
                                      <div className="text-sm text-gray-600">Waves</div>
                                      <div className="font-medium">{report.conditions.waves}</div>
                                    </div>
                                  )}
                                  {report.conditions.surge && (
                                    <div className="p-3 bg-blue-50 rounded-lg">
                                      <div className="text-sm text-gray-600">Surge</div>
                                      <div className="font-medium">{report.conditions.surge}</div>
                                    </div>
                                  )}
                                  {report.conditions.visibility && (
                                    <div className="p-3 bg-cyan-50 rounded-lg">
                                      <div className="text-sm text-gray-600">Visibility</div>
                                      <div className="font-medium">
                                        {report.conditions.visibility} {report.conditions.visibility_unit || 'm'}
                                      </div>
                                    </div>
                                  )}
                                </div>
                              </SectionCard>
                            )}

                            {/* Marine Life */}
                            {report.marine_life_observations?.length > 0 && (
                              <SectionCard title="Marine Life Observed" icon={Fish}>
                                <div className="flex flex-wrap gap-2">
                                  {report.marine_life_observations.map((obs, idx) => (
                                    <span 
                                      key={idx}
                                      className="px-3 py-1 bg-cyan-100 text-cyan-700 rounded-full text-sm"
                                    >
                                      {typeof obs === 'string' ? obs : obs.species_name || obs}
                                    </span>
                                  ))}
                                </div>
                              </SectionCard>
                            )}

                            {/* Rating */}
                            {report.rating && (
                              <SectionCard title="Site Rating" icon={Star}>
                                {renderStars(report.rating)}
                              </SectionCard>
                            )}

                            {/* Site Photos */}
                            {report.site_photos?.length > 0 && (
                              <SectionCard title="Site Photos" icon={Camera}>
                                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                                  {report.site_photos.map((photo, idx) => (
                                    <div key={idx} className="relative">
                                      <img 
                                        src={getImageUrl(photo.url || photo)}
                                        alt={`Site photo ${idx + 1}`}
                                        className="w-full h-32 object-cover rounded-lg"
                                      />
                                      {photo.hide_from_gallery && (
                                        <div className="absolute bottom-1 right-1 bg-black/50 rounded px-1 py-0.5">
                                          <Eye className="w-3 h-3 text-white" />
                                        </div>
                                      )}
                                    </div>
                                  ))}
                                </div>
                              </SectionCard>
                            )}

                            {/* Species ID Request */}
                            {report.species_id_requested && report.species_id_photo_urls?.length > 0 && (
                              <SectionCard title="Species ID Requested" icon={Fish}>
                                <div className="text-sm text-amber-600 mb-2">
                                  {report.species_id_photo_urls.length} photo(s) submitted for identification
                                </div>
                                <div className="grid grid-cols-3 gap-2">
                                  {report.species_id_photo_urls.map((url, idx) => (
                                    <img 
                                      key={idx}
                                      src={getImageUrl(url)}
                                      alt={`Species ID ${idx + 1}`}
                                      className="w-full h-20 object-cover rounded border-2 border-amber-300"
                                    />
                                  ))}
                                </div>
                              </SectionCard>
                            )}
                          </TabsContent>

                          {/* NOTES TAB */}
                          <TabsContent value="notes" className="mt-4 space-y-4">
                            {/* Dive Center */}
                            {report.dive_center_name && (
                              <SectionCard title="Dive Center" icon={Building2}>
                                <div className="text-lg font-medium">{report.dive_center_name}</div>
                              </SectionCard>
                            )}

                            {/* Buddies */}
                            {report.buddies?.length > 0 && (
                              <SectionCard title="Dive Buddies" icon={User}>
                                <div className="flex flex-wrap gap-2">
                                  {report.buddies.map((buddy, idx) => (
                                    <span 
                                      key={idx}
                                      className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm"
                                    >
                                      {buddy.user_name || buddy}
                                    </span>
                                  ))}
                                </div>
                              </SectionCard>
                            )}

                            {/* Notes */}
                            {report.notes && (
                              <SectionCard title="Notes" icon={FileText}>
                                <div className="whitespace-pre-wrap text-gray-700">{report.notes}</div>
                              </SectionCard>
                            )}

                            {/* Show message if no notes data */}
                            {!report.dive_center_name && !report.buddies?.length && !report.notes && (
                              <div className="text-center text-gray-500 py-8">
                                No notes recorded for this dive
                              </div>
                            )}
                          </TabsContent>
                        </Tabs>

                        {/* Action Buttons */}
                        <div className="flex gap-2 pt-4 border-t">
                          <Button variant="outline" size="sm" onClick={() => handleEdit(report)}>
                            <Edit2 className="w-4 h-4 mr-1" />
                            Edit
                          </Button>
                          <Button 
                            variant="outline" 
                            size="sm" 
                            onClick={() => handleDelete(report.id)}
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          >
                            <Trash2 className="w-4 h-4 mr-1" />
                            Delete
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => window.open(`/profile/${report.user_id}`, '_blank')}
                          >
                            <Eye className="w-4 h-4 mr-1" />
                            View User Profile
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default DiveReportsManagement;
